import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchWrapper } from "../util/fetchUtil";
import {
    PoliticalExposureInformation,
    PoliticalExposurePoliticalAffiliateInformation
} from "../model/PoliticalExposureType";
import { CreateZahtjevKontaktAdresaRequest } from "../model/ContactAddressType";

export type ConsentData = {
    analyticsConsent: boolean | null;
    marketingConsent: boolean | null;
    analyticsUserIdFromSession: string | null
}

export type CreateZahtjevRequest = {
    oib: string;
    recaptchaToken: string;
    consentData: ConsentData;
};

export type ZahtjevValidation = {
    status: "INVALID_KON_OC" | "INVALID_HASH" | "INVALID_DATA" | string | undefined;
    message: string;
}

export type CreateZahtjevResponse = {
    zahtjevId: number;
    accessToken: string;
    status: string;
    uploadId: number;
    uploadHashKey: string;
    analyticsUserId: string;
};

export type CreateZahtjevOsobniPodaciRequest = {
    spol: string;
    ime: string;
    prezime: string;
    datumRodjenja: string;
    mjestoRodjenja: string;
    drzavaRodjenja: string;
    drzavaRodjenjaRegija?: string | null;
    drzavljanstvo: string;
    drzavljanstvoRegija?: string | null;
    imaDrugoDrzavljanstvo: boolean;
    drzavljanstvoDrugo?: string | null;
    drzavljanstvoDrugoRegija?: string | null;
    telefonPredbroj?: string | null;
    telefon?: string | null;
    mobitelPredbroj?: string | null;
    mobitel?: string | null;
    mailAdresa?: string | null;
    ulica?: string | null;
    kucniBr?: string | null;
    mjesto?: string | null;
    postanskiBroj?: string | null;
    drzava?: string | null;
    dostavaDrugaAdresa?: boolean | null;
    dostavaUlica?: string | null;
    dostavaKucniBr?: string | null;
    dostavaMjesto?: string | null;
    dostavaPostanskiBroj?: string | null;
    dostavaDrzava?: string | null;
    idDokumentVrsta: string;
    idDokumentBroj: string;
    idDokumentIzdavatelj: string;
    idDokumentDrzavaIzdavanja: string;
    idDokumentDrzavaIzdavanjaRegija?: string | null;
    idDokumentDatumVazenja?: string | null;
    idDokumentTrajnoVazenje: boolean;
}

export type CreateZahtjevFinancijskiPodaciRequest = {
    statusZaposlenja: string;
    vrstaPoslodavca: string | null;
    prosjecnaMjesecnaPrimanjaRedovna: string;
    prosjecanIznosOstalihPrihoda: string | null;
    pozicijaZaposlenja: string | null;
    svrhaOtvaranjaRacuna: string;
    izvorOstalihPrihoda: string;
    vrsteTransakcijaNaRacunu: string;
    prosjecneGotovinskeTransakcije?: string | null;
    ocekivaniGodisnjiPromet: string;
    osnovaPriljevaSredstava: string;
    drzavePrekogranicnoPlacanje: string | null;
}

export type DrzavaOporezivanja = {
    drzava: string;
    porezniBroj: string;
}

export type CreateZahtjevPoreznaRezidentnostRequest = {
    imaDodatnoAmerickoDrzavljanstvo: boolean;
    zelenaKarta: boolean;
    porezniObveznikSAD: boolean;
    posjedujePorezniBrojSAD: boolean;
    porezniBrojSAD: string | null;
    drzaveOporezivanja: DrzavaOporezivanja[] | null;
}

export type CreateZahtjevMarketinskaPrivolaRequest = {
    privolaMarketinskeInformacije: boolean;
    privolaPersonaliziranePonude: boolean;
    kanaliDostave: string;
}

export type PolitickaOsoba = PoliticalExposurePoliticalAffiliateInformation;

export type CreateZahtjevPolitickaIzlozenostRequest = {
    celnikDrzava: boolean;
    clanZakonodavnogTijela: boolean;
    clanUpravnogTijelaPolitika: boolean;
    sudVrhovniUstavni: boolean;
    sudRevizorski: boolean;
    clanSavjetaSredisnjeBanke: boolean;
    diplomat: boolean;
    clanNadzornogOdbora: boolean;
    clanMedjunarodnaOrganizacija: boolean;
    celnikLokalnaVlast: boolean;
    clanBracni: boolean;
    clanPotomak: boolean;
    clanRoditelj: boolean;
    suradnikPoslovniZajedno: boolean;
    suradnikPoslovni: boolean;
    izvorSredstava?: string | null;
    izvorSredstavaBanka?: string | null;
    politickeOsobe: PolitickaOsoba[];
}

export type CreateZahtjevDostavaDokumentacijeRequest = {
    kanalRacKart: string;
    kanalKredPrek: string;
    kanalOpciDok: string;
}

export type CreateZahtjevZavrsetakRequest = {
    language: "HR" | "EN";
};

export type ZahtjevResponseDostavaDokumentacije = {
    kanalRacKart: string;
    kanalKredPrek: string;
    kanalOpciDok: string;
};

export type ZahtjevResponseMarketinskaPrivola = {
    privolaMarketinskeInformacije: false;
    privolaPersonaliziranePonude: false;
    kanaliDostave: string;
};

export type ZahtjevResponsePolitickaIzlozenost = PoliticalExposureInformation;

export type ZahtjevResponseOsobniPodaci = {
    spol: string;
    ime: string;
    prezime: string;
    datumRodjenja: string;
    mjestoRodjenja: string;
    drzavaRodjenja: string;
    drzavaRodjenjaRegija: string;
    drzavljanstvo: string;
    drzavljanstvoRegija: string;
    imaDrugoDrzavljanstvo: boolean;
    drzavljanstvoDrugo: string;
    drzavljanstvoDrugoRegija: string;
    telefonPredbroj: string;
    telefon: string;
    mobitelPredbroj: string;
    mobitel: string;
    mailAdresa: string;
    ulica: string;
    kucniBr: string;
    mjesto: string;
    postanskiBroj: string;
    drzava: string;
    drzavaRegija: string;
    dostavaDrugaAdresa: boolean;
    dostavaUlica: string;
    dostavaKucniBr: string;
    dostavaMjesto: string;
    dostavaPostanskiBroj: string;
    dostavaDrzava: string;
    dostavaDrzavaRegija: string;
    boraviste: boolean;
    boravisteUlica: string;
    boravisteKucniBr: string;
    boravisteMjesto: string;
    boravistePostanskiBroj: string;
    boravisteDrzava: string;
    boravisteDrzavaRegija: string;
    trecaAdresa?: boolean;
    trecaUlica?: string;
    trecaKucniBr?: string;
    trecaMjesto?: string;
    trecaPostanskiBroj?: string;
    trecaDrzava?: string;
    trecaDrzavaRegija?: string;
    adresaZaDostavuDokumentacije?: string;
    idDokumentVrsta: string;
    idDokumentBroj: string;
    idDokumentIzdavatelj: string;
    idDokumentDrzavaIzdavanja: string;
    idDokumentDrzavaIzdavanjaRegija: string;
    idDokumentDatumVazenja: string;
    idDokumentTrajnoVazenje: boolean;
}

export type ZahtjevResponsePoreznaRezidentnost = {
    imaDodatnoAmerickoDrzavljanstvo: boolean;
    zelenaKarta: boolean;
    porezniObveznikSAD: boolean;
    posjedujePorezniBrojSAD: boolean;
    porezniBrojSAD: string;
    drzaveOporezivanja: DrzavaOporezivanja[] | null;
}

export type ZahtjevResponseFinancijskeInformacije = {
    statusZaposlenja: string;
    pozicijaZaposlenja: string;
    vrstaPoslodavca: string;
    prosjecnaMjesecnaPrimanjaRedovna: string;
    prosjecanIznosOstalihPrihoda: string;
    prosjecneGotovinskeTransakcije: string;
    ocekivaniGodisnjiPromet: string;
    izvorOstalihPrihoda: string;
    vrsteTransakcijaNaRacunu: string;
    osnovaPriljevaSredstava: string;
    svrhaOtvaranjaRacuna: string;
    drzavePrekogranicnoPlacanje: string;
}

export type ZahtjevResponse = {
    id: number;
    oib: string;
    status: string;
    dostavaDokumentacije: ZahtjevResponseDostavaDokumentacije,
    marketinskaPrivola: ZahtjevResponseMarketinskaPrivola,
    politickaIzlozenost: ZahtjevResponsePolitickaIzlozenost,
    osobniPodaci: ZahtjevResponseOsobniPodaci,
    poreznaRezidentnost: ZahtjevResponsePoreznaRezidentnost,
    financijskiPodaci: ZahtjevResponseFinancijskeInformacije,
}

const queryData = {
    zahtjev: {
        registracija: {
            path: `/zahtjev/registracija`,
        },
        ucitan_dokument: {
            path: (zahtjevId: number) => `/zahtjev/${zahtjevId}/ucitan-dokument`,
        },
        osobni_podaci: {
            path: (zahtjevId: number) => `/zahtjev/${zahtjevId}/osobni-podaci`,
        },
        kontakt_adresa: {
            path: (zahtjevId: number) => `/zahtjev/${zahtjevId}/kontakt-adresa`,
        },
        financije: {
            path: (zahtjevId: number) => `/zahtjev/${zahtjevId}/financije`,
        },
        porezna_rezidentnost: {
            path: (zahtjevId: number) => `/zahtjev/${zahtjevId}/porezna-rezidentnost`,
        },
        politicka_izlozenost: {
            path: (zahtjevId: number) => `/zahtjev/${zahtjevId}/politicka-izlozenost`,
        },
        dostava_dokumentacije: {
            path: (zahtjevId: number) => `/zahtjev/${zahtjevId}/dostava-dokumentacije`,
        },
        marketinska_privola: {
            path: (zahtjevId: number) => `/zahtjev/${zahtjevId}/marketinska-privola`,
        },
        zavrsetak: {
            path: (zahtjevId: number) => `/zahtjev/${zahtjevId}/zavrsetak`,
        },
        zahtjev: {
            path: (zahtjevId: number) => `/zahtjev/${zahtjevId}`,
            queryKey: (zahtjevId: number) => ["getZahtjev", zahtjevId],
        },
    },
};

async function registerUser(
  createZahtjevRequest: CreateZahtjevRequest
): Promise<CreateZahtjevResponse | ZahtjevValidation> {
    let response = await fetch(`${process.env.REACT_APP_SERVICE_URL}${queryData.zahtjev.registracija.path}`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json; charset=utf-8",
        },
        body: JSON.stringify(createZahtjevRequest),
    });

    if (!response.ok && response.status !== 400) {
        throw new Error(`Error ${response.status} ${response.statusText}.`);
    }

    return response.json();
}

export function useRegisterUser() {
    return useMutation<CreateZahtjevResponse | ZahtjevValidation, Error, CreateZahtjevRequest, unknown>({
        mutationFn: (createZahtjevRequest: CreateZahtjevRequest) =>
          registerUser(createZahtjevRequest),
    });
}

async function uploadDocument(
  zahtjevId: number
): Promise<string> {
    return fetchWrapper(
      queryData.zahtjev.ucitan_dokument.path(zahtjevId),
      "POST",
    );
}

export function useUploadDocument(zahtjevId: number) {
    return useMutation<string, Error, unknown>({
        mutationFn: () =>
          uploadDocument(zahtjevId),
    });
}

async function updatePersonalData(
  zahtjevId: number,
  request: CreateZahtjevOsobniPodaciRequest,
): Promise<string> {
    return fetchWrapper(
      queryData.zahtjev.osobni_podaci.path(zahtjevId),
      "POST",
      request,
    );
}

export function useUpdatePersonalData(zahtjevId: number) {
    return useMutation<string, Error, CreateZahtjevOsobniPodaciRequest, unknown>({
        mutationFn: (request: CreateZahtjevOsobniPodaciRequest) =>
          updatePersonalData(zahtjevId, request),
    });
}

async function updateContactAddress(
  zahtjevId: number,
  request: CreateZahtjevKontaktAdresaRequest,
): Promise<string> {
    return fetchWrapper(
      queryData.zahtjev.kontakt_adresa.path(zahtjevId),
      "POST",
      request,
    );
}

export function useUpdateContactAddress(zahtjevId: number) {
    return useMutation<string, Error, CreateZahtjevKontaktAdresaRequest, unknown>({
        mutationFn: (request: CreateZahtjevKontaktAdresaRequest) =>
          updateContactAddress(zahtjevId, request),
    });
}

async function updateFinanceData(
  zahtjevId: number,
  request: CreateZahtjevFinancijskiPodaciRequest,
): Promise<string> {
    return fetchWrapper(
      queryData.zahtjev.financije.path(zahtjevId),
      "POST",
      request,
    );
}

export function useUpdateFinanceData(zahtjevId: number) {
    return useMutation<string, Error, CreateZahtjevFinancijskiPodaciRequest, unknown>({
        mutationFn: (request: CreateZahtjevFinancijskiPodaciRequest) =>
          updateFinanceData(zahtjevId, request),
    });
}

async function updateTaxResidency(
  zahtjevId: number,
  request: CreateZahtjevPoreznaRezidentnostRequest
): Promise<string> {
    return fetchWrapper(
      queryData.zahtjev.porezna_rezidentnost.path(zahtjevId),
      "POST",
      request,
    );
}

export function useUpdateTaxResidency(zahtjevId: number) {
    return useMutation<string, Error, CreateZahtjevPoreznaRezidentnostRequest, unknown>({
        mutationFn: (request: CreateZahtjevPoreznaRezidentnostRequest) =>
          updateTaxResidency(zahtjevId, request),
    });
}

async function updateMarketingConsent(
  zahtjevId: number,
  request: CreateZahtjevMarketinskaPrivolaRequest
): Promise<string> {
    return fetchWrapper(
      queryData.zahtjev.marketinska_privola.path(zahtjevId),
      "POST",
      request,
    );
}

export function useUpdateMarketingConsent(zahtjevId: number) {
    return useMutation<string, Error, CreateZahtjevMarketinskaPrivolaRequest, unknown>({
        mutationFn: (request: CreateZahtjevMarketinskaPrivolaRequest) =>
          updateMarketingConsent(zahtjevId, request),
    });
}

async function updatePoliticalExposure(
  zahtjevId: number,
  request: CreateZahtjevPolitickaIzlozenostRequest
): Promise<string> {
    return fetchWrapper(
      queryData.zahtjev.politicka_izlozenost.path(zahtjevId),
      "POST",
      request,
    );
}

export function useUpdatePoliticalExposure(zahtjevId: number) {
    return useMutation<string, Error, CreateZahtjevPolitickaIzlozenostRequest, unknown>({
        mutationFn: (request: CreateZahtjevPolitickaIzlozenostRequest) =>
          updatePoliticalExposure(zahtjevId, request),
    });
}

async function updateDocumentationDelivery(
  zahtjevId: number,
  request: CreateZahtjevDostavaDokumentacijeRequest
): Promise<string> {
    return fetchWrapper(
      queryData.zahtjev.dostava_dokumentacije.path(zahtjevId),
      "POST",
      request,
    );
}

export function useUpdateDocumentationDelivery(zahtjevId: number) {
    return useMutation<string, Error, CreateZahtjevDostavaDokumentacijeRequest, unknown>({
        mutationFn: (request: CreateZahtjevDostavaDokumentacijeRequest) =>
          updateDocumentationDelivery(zahtjevId, request),
    });
}

async function finishRegistration(
  zahtjevId: number,
  request: CreateZahtjevZavrsetakRequest
): Promise<string> {
    return fetchWrapper(
      queryData.zahtjev.zavrsetak.path(zahtjevId),
      "POST",
      request,
    );
}

export function useFinishRegistration(zahtjevId: number) {
    return useMutation<string, Error, CreateZahtjevZavrsetakRequest>({
        mutationFn: (request: CreateZahtjevZavrsetakRequest) =>
          finishRegistration(zahtjevId, request),
    });
}

async function getZahtjev(
  zahtjevId: number
): Promise<ZahtjevResponse> {
    return fetchWrapper(
      queryData.zahtjev.zahtjev.path(zahtjevId),
      "GET",
    );
}

export function useGetZahtjev(zahtjevId: number) {
    return useQuery<ZahtjevResponse>({
        queryKey: queryData.zahtjev.zahtjev.queryKey(zahtjevId),
        queryFn: () => getZahtjev(zahtjevId),
        enabled: false
    });
}

